/** The right side of a record's header (the booking, order, user and catalog records): the
 *  Actions menu, then **Edit** (`E`) or, in edit mode, **Close editor** (`Esc`).
 *
 *  - May edit now (`canEdit`) → Edit.
 *  - May not, with a reason (`editBlocked`: a deleted user, editing switched off) → Edit
 *    shown disabled, the reason in its tab stop (`BlockedEditButton`).
 *  - Otherwise no Edit at all: a disabled one would advertise power the operator lacks.
 *
 *  The Actions menu's `note` keeps a reason in reach of the keyboard as well (the kill
 *  switch). */
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Box, Button } from '@mui/material';

import { Kbd } from '@/features/records/page/Kbd';
import {
  RecordActionsMenu,
  type RecordActionGroup,
} from '@/features/records/page/RecordActionsMenu';

import { BlockedEditButton } from './BlockedEditButton';

export interface RecordHeaderActionsProps {
  /** The Actions menu's groups. */
  groups: RecordActionGroup[];
  /** The Actions menu's note. */
  note?: string | null;
  /** The operator may edit the record now. */
  canEdit: boolean;
  editing: boolean;
  /** Without `canEdit`: Edit is shown disabled with this reason. */
  editBlocked?: string | null;
  onEdit?: () => void;
  onClose?: () => void;
}

export function RecordHeaderActions({
  groups,
  note,
  canEdit,
  editing,
  editBlocked,
  onEdit,
  onClose,
}: RecordHeaderActionsProps) {
  return (
    // The buttons wrap among themselves before they overflow a narrow (390 px) page.
    <Box
      data-record-header-actions
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        flexWrap: 'wrap',
        gap: 1,
        flex: 'none',
        maxWidth: '100%',
        '& .MuiButton-root': { whiteSpace: 'nowrap' },
      }}
    >
      <RecordActionsMenu groups={groups} {...(note ? { note } : {})} />
      {editing ? (
        <Button variant="outlined" size="small" onClick={onClose} data-close-editor>
          Close editor
          <Kbd>Esc</Kbd>
        </Button>
      ) : canEdit ? (
        <Button
          variant="contained"
          size="small"
          startIcon={<EditOutlinedIcon />}
          onClick={onEdit}
          data-edit
        >
          Edit
          <Kbd>E</Kbd>
        </Button>
      ) : editBlocked ? (
        <BlockedEditButton reason={editBlocked} />
      ) : null}
    </Box>
  );
}
